import React from 'react'
import type { FormikHelpers } from 'formik'

import type { OnSubmit } from './types'

interface InitialState {
  status: 'init'
  handleCancel: null
  handleConfirm: null
}

interface ConfirmingState {
  status: 'confirming'
  handleCancel: () => void
  handleConfirm: () => void
}

type State = InitialState | ConfirmingState

const initialState: InitialState = {
  status: 'init',
  handleCancel: null,
  handleConfirm: null
}

enum ActionTypes {
  AskConfirmation,
  CleanUp
}

interface AskConfirmation {
  type: ActionTypes.AskConfirmation
  handleCancel: () => void
  handleConfirm: () => void
}

interface CleanUp {
  type: ActionTypes.CleanUp
}

type Actions = AskConfirmation | CleanUp

function submitConfirmationReducer(state: State, action: Actions): State {
  switch (action.type) {
    case ActionTypes.AskConfirmation:
      return {
        status: 'confirming',
        handleCancel: action.handleCancel,
        handleConfirm: action.handleConfirm
      }
    case ActionTypes.CleanUp:
      return {
        status: 'init',
        handleCancel: null,
        handleConfirm: null
      }
    default:
      return state
  }
}

export function isConfirming<Values, Result extends Values>(
  values: InitialValues<Values, Result> | ConfirmingValues<Values, Result>
): values is ConfirmingValues<Values, Result> {
  return values.status === 'confirming'
}

interface InitialValues<Values, Result extends Values> extends InitialState {
  handleSubmit: (values: Values, formikHelpers: FormikHelpers<Values>) => Promise<Result | void>
}

interface ConfirmingValues<Values, Result extends Values> extends ConfirmingState {
  handleSubmit: (values: Values, formikHelpers: FormikHelpers<Values>) => Promise<Result | void>
}

export function useSubmitConfirmation<Values, Result extends Values>(
  submitHandler: OnSubmit<Values, Result>,
  confirmationRequired: boolean
) {
  const [state, dispatch] = React.useReducer(submitConfirmationReducer, initialState)

  const handleSubmit = React.useCallback(
    async (values, formikHelpers): Promise<Result | void> => {
      const confirmation = new Promise<Result>((resolve, reject) => {
        dispatch({
          type: ActionTypes.AskConfirmation,
          handleCancel: () => reject(),
          handleConfirm: () => resolve(submitHandler(values, formikHelpers))
        })
      })

      try {
        const result = await confirmation

        dispatch({ type: ActionTypes.CleanUp })

        return result
      } catch {
        dispatch({ type: ActionTypes.CleanUp })
      }
    },
    [submitHandler]
  )

  return {
    handleCancel: state.handleCancel,
    handleConfirm: state.handleConfirm,
    handleSubmit: confirmationRequired ? handleSubmit : submitHandler,
    status: state.status
  } as InitialValues<Values, Result> | ConfirmingValues<Values, Result>
}
